import React, { useRef, useState } from 'react';
import { Upload, Link as LinkIcon, Loader2, ImageOff, X } from 'lucide-react';
import { Input } from './ui/input';
import { uploadsAPI } from '../services/api';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const MAX_SIZE = 5 * 1024 * 1024;

const ImageUploadField = ({ value, onChange, required = false, testId = 'image', label = 'Image' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [broken, setBroken] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    if (!ACCEPT.split(',').includes(file.type)) { setError('Format non supporté (JPG, PNG, WebP ou GIF)'); return; }
    if (file.size > MAX_SIZE) { setError('Image trop volumineuse (5 Mo maximum)'); return; }
    setUploading(true);
    setProgress(0);
    try {
      const result = await uploadsAPI.uploadImage(file, setProgress);
      setBroken(false);
      onChange(result.url);
    } catch (e) {
      setError(e.response?.data?.detail || "Échec de l'envoi de l'image");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid={`${testId}-upload-field`}>
      {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label} {required && '*'}</label>}
      <div className="flex gap-3">
        <div
          data-testid={`${testId}-preview`}
          className="w-20 h-20 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0"
        >
          {value && !broken ? (
            <img src={uploadsAPI.resolveUrl(value)} alt="" className="w-full h-full object-contain" onError={() => setBroken(true)} />
          ) : (
            <ImageOff size={20} className="text-gray-300" />
          )}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <div
            data-testid={`${testId}-dropzone`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            onClick={() => !uploading && inputRef.current?.click()}
            className="flex items-center gap-2 border border-dashed border-gray-300 rounded-md px-3 py-2 text-sm text-gray-600 cursor-pointer hover:border-orange-400 hover:bg-orange-50/40 transition-colors"
          >
            {uploading ? <Loader2 size={16} className="animate-spin text-orange-500" /> : <Upload size={16} className="text-orange-500" />}
            <span className="flex-1 truncate">
              {uploading ? `Envoi… ${progress}%` : 'Choisir un fichier ou glisser-déposer (JPG, PNG, WebP, GIF · 5 Mo max)'}
            </span>
            <input
              ref={inputRef}
              data-testid={`${testId}-file-input`}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>
          <div className="flex items-center gap-2">
            <LinkIcon size={14} className="text-gray-400 flex-shrink-0" />
            <Input
              data-testid={`${testId}-url-input`}
              value={value}
              onChange={(e) => { setBroken(false); onChange(e.target.value); }}
              placeholder="ou coller une URL https://..."
              className="h-8 text-sm"
            />
            {value && (
              <button type="button" data-testid={`${testId}-clear`} onClick={() => { setBroken(false); onChange(''); }} className="p-1 text-gray-400 hover:text-red-600" title="Retirer l'image">
                <X size={14} />
              </button>
            )}
          </div>
          {error && <p data-testid={`${testId}-upload-error`} className="text-xs text-red-600">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default ImageUploadField;
